import { TutorialDefinition } from '../TutorialEngine.js';
import { FloodRenderOptions } from './FloodTutorialAdapter.js';
import { COLORS } from '../../rendering/Renderer.js';

type FloodScene = {
    gameState: string;
    renderOptions: FloodRenderOptions;
    annotations: TutorialDefinition['scenes'][number]['annotations'];
    animate?: {
        interval: number;
        loop: boolean;
        frames: string[];
    };
};

// Grid: 6/1-3:1D1,2A2,3E3
// Digit 1 at D1 (col 3, row 1)
// Digit 2 at A2 (col 0, row 2)
// Digit 3 at E3 (col 4, row 3)
// Full solution: D,D0E0,D1E1,D1D2,C1C2,B1C1,B0B1,A2B2,A2A3

const scenes: FloodScene[] = [
    // Scene 1: Goal — what a solved puzzle looks like
    {
        gameState: 'D,D0E0,D1E1,D1D2,C1C2,B1C1,B0B1,A2B2,A2A3',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'In Flood, fill all digits\nin order with water', cell: 'C4', anchor: 'center', color: COLORS.teal },
        ],
    },

    // Scene 2: Empty room — progressive fill animation, fill times and badge colors
    {
        gameState: 'A@18',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Water flows down\nfrom the faucet', cell: 'B0', anchor: 'near-right' },
            { type: 'text-label', text: 'Numbers show\nfill times', cell: 'C5', anchor: 'center' },
            { type: 'text-label', text: 'Teal badge =\ncorrect order', cell: 'C2', anchor: 'center', color: COLORS.teal },
            { type: 'text-label', text: 'Amber badge =\nincorrect order', cell: 'D4', anchor: 'center', color: COLORS.amber },
        ],
        animate: {
            interval: 2000,
            loop: true,
            frames: [
                'A@0',
                'A@6',
                'A@12',
                'A@18',
                'A@24',
                'A',
            ],
        },
    },

    // Scene 3: Placing walls, moving the faucet
    {
        gameState: 'E,C5D5',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click top edge\nto move faucet', cell: 'B1', anchor: 'center' },
            { type: 'text-label', text: 'Click cell edges\nto place walls', cell: 'C3', anchor: 'center' },
            { type: 'pulse', edge: '1020', color: COLORS.teal },
            { type: 'pulse', edge: '3435', color: COLORS.teal },
        ],
        animate: {
            interval: 2000,
            loop: true,
            frames: [
                'E,C5D5',
                'B,C5D5',
                'B,C5D5,C4D4',
                'E,C5D5,C4D4',
            ],
        },
    },

    // Scene 4: Basic split — water divides around walls
    {
        gameState: 'D,D5E5,D4E4,D3D4,E3E4',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Water splits evenly\non flat surfaces', cell: 'C2', anchor: 'center' },
        ],
        animate: {
            interval: 2000,
            loop: true,
            frames: [
                'D,D5E5,D4E4,D3D4,E3E4@4',
                'D,D5E5,D4E4,D3D4,E3E4@8',
                'D,D5E5,D4E4,D3D4,E3E4@12',
                'D,D5E5,D4E4,D3D4,E3E4@18',
                'D,D5E5,D4E4,D3D4,E3E4',
            ],
        },
    },

    // Scene 5: Siphons and overflow — water rises over walls
    {
        gameState: 'A,B0C0,B1C1,B2C2,B3C3,C3C4,D3D4,D3E3,D2E2,E2F2,E3F3,E4F4,E5F5',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Water rises\nand overflows walls', cell: 'C5', anchor: 'center' },
        ],
        animate: {
            interval: 2000,
            loop: true,
            frames: [
                'A,B0C0,B1C1,B2C2,B3C3,C3C4,D3D4,D3E3,D2E2,E2F2,E3F3,E4F4,E5F5@5',
                'A,B0C0,B1C1,B2C2,B3C3,C3C4,D3D4,D3E3,D2E2,E2F2,E3F3,E4F4,E5F5@10',
                'A,B0C0,B1C1,B2C2,B3C3,C3C4,D3D4,D3E3,D2E2,E2F2,E3F3,E4F4,E5F5@16',
                'A,B0C0,B1C1,B2C2,B3C3,C3C4,D3D4,D3E3,D2E2,E2F2,E3F3,E4F4,E5F5@20',
                'A,B0C0,B1C1,B2C2,B3C3,C3C4,D3D4,D3E3,D2E2,E2F2,E3F3,E4F4,E5F5@24',
                'A,B0C0,B1C1,B2C2,B3C3,C3C4,D3D4,D3E3,D2E2,E2F2,E3F3,E4F4,E5F5@30',
                'A,B0C0,B1C1,B2C2,B3C3,C3C4,D3D4,D3E3,D2E2,E2F2,E3F3,E4F4,E5F5',
            ],
        },
    },

    // Scene 6: Flow block (sealed area) and unconnected edges
    {
        gameState: 'D,B0C0,B2C2,B1C1,B3C3,C3C4,D3D4,D3E3,D2E2,E1E2,F1F2',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Walls cannot\ncreate sealed areas', cell: 'B4', anchor: 'center', color: COLORS.amber },
            { type: 'text-label', text: 'Walls must connect\nto the grid edge', cell: 'E5', anchor: 'center', color: COLORS.amber },
        ],
        animate: {
            interval: 3000,
            loop: true,
            frames: [
                'D,B0C0,B2C2,B1C1,B3C3,C3C4,D3D4,D3E3,D2E2,E1E2,F1F2',
                'D,B2C2,B1C1,B3C3,C3C4,D3D4,D3E3,D2E2,E1E2',
            ],
        },
    },

];

/**
 * Flood tutorial: 6 scenes on a 6x6 grid with digits 1-3.
 * See docs/tutorials/flood.md for detailed scene descriptions.
 */
export const FLOOD_TUTORIAL: TutorialDefinition = {
    gameType: 'flood',
    gridCode: '6/1-3:1D1,2A2,3E3',
    scenes,
};
